import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import Field, { inputClass } from '@/components/base/Field';
import { EXAM_TYPES, GRADES, MONTHS, SUBJECT_GROUPS, SUBJECT_OPTIONS } from '@/lib/subjects';

interface ExamSessionFormProps {
  studentId: string;
  onCreated: () => void;
  onClose: () => void;
}

export default function ExamSessionForm({ studentId, onCreated, onClose }: ExamSessionFormProps) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [grade, setGrade] = useState('고1');
  const [examType, setExamType] = useState('중간고사');
  const [semester, setSemester] = useState('1학기');
  const [month, setMonth] = useState('3월');
  const [selected, setSelected] = useState<string[]>([]);
  const [custom, setCustom] = useState('');
  const [keyword, setKeyword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isMock = examType === '모의고사';

  const toggle = (subject: string) => {
    setSelected((prev) =>
      prev.includes(subject) ? prev.filter((s) => s !== subject) : [...prev, subject],
    );
  };

  const addCustom = () => {
    const name = custom.trim();
    if (!name) return;
    if (!selected.includes(name)) setSelected((prev) => [...prev, name]);
    setCustom('');
  };

  const kw = keyword.trim();
  const filteredGroups = SUBJECT_GROUPS.map((g) => ({
    category: g.category,
    subjects: g.subjects.filter((s) => s.includes(kw)),
  })).filter((g) => g.subjects.length > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!year.trim()) {
      setError('연도를 입력해주세요.');
      return;
    }
    if (selected.length === 0) {
      setError('과목을 한 개 이상 선택해주세요.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = selected.map((subject) => ({
        student_id: studentId,
        exam_type: examType,
        year: year.trim(),
        grade,
        semester: isMock ? '' : semester,
        month: isMock ? month : null,
        subject,
        score: '',
        standard_score: '',
        percentile: '',
        rank_grade: '',
      }));
      const { error: insertError } = await supabase.from('exam_scores').insert(payload);
      if (insertError) throw insertError;
      onCreated();
      onClose();
    } catch {
      setError('시험 저장에 실패했어요.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-5 rounded-lg border border-background-200 bg-background-100 p-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="연도" required>
          <input
            type="text"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            placeholder="예: 2026"
            className={inputClass}
          />
        </Field>
        <Field label="학년">
          <select value={grade} onChange={(e) => setGrade(e.target.value)} className={inputClass}>
            {GRADES.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </Field>
        <Field label="시험 종류">
          <select value={examType} onChange={(e) => setExamType(e.target.value)} className={inputClass}>
            {EXAM_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        {isMock ? (
          <Field label="월">
            <select value={month} onChange={(e) => setMonth(e.target.value)} className={inputClass}>
              {MONTHS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <Field label="학기">
            <select value={semester} onChange={(e) => setSemester(e.target.value)} className={inputClass}>
              <option value="1학기">1학기</option>
              <option value="2학기">2학기</option>
            </select>
          </Field>
        )}
      </div>

      <div className="mt-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold text-foreground-950">
            시험 본 과목 선택
            <span className="ml-2 text-xs font-normal text-foreground-500">
              {selected.length}과목 선택됨
            </span>
          </p>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5">
              <i className="ri-search-line text-foreground-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="과목 검색"
                className="w-28 border-none bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
            </div>
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => setSelected([])}
                className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-semibold text-foreground-600 transition hover:bg-background-200 whitespace-nowrap"
              >
                선택 해제
              </button>
            )}
          </div>
        </div>

        <div className="mt-3 max-h-60 overflow-y-auto rounded-lg border border-background-200 bg-background-50 p-3">
          {filteredGroups.length === 0 ? (
            <p className="py-4 text-center text-xs text-foreground-500">
              검색 결과가 없어요. 아래에서 직접 추가해보세요.
            </p>
          ) : (
            <div className="flex flex-col gap-3">
              {filteredGroups.map((group, gi) => (
                <div
                  key={group.category}
                  className={gi > 0 ? 'border-t border-background-200 pt-3' : ''}
                >
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-foreground-600">
                    <i
                      className={
                        group.category === '중학교 과목'
                          ? 'ri-book-open-line text-secondary-600'
                          : 'ri-graduation-cap-line text-primary-600'
                      }
                    ></i>
                    {group.category}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {group.subjects.map((s) => {
                      const active = selected.includes(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => toggle(s)}
                          className={`flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition whitespace-nowrap ${
                            active
                              ? 'border-primary-500 bg-primary-500 text-background-50'
                              : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:bg-primary-100/60'
                          }`}
                        >
                          {active && <i className="ri-check-line"></i>}
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selected.filter((s) => !SUBJECT_OPTIONS.includes(s)).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {selected
              .filter((s) => !SUBJECT_OPTIONS.includes(s))
              .map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggle(s)}
                  className="flex items-center gap-1 rounded-full border border-accent-300 bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800 transition hover:bg-accent-200 whitespace-nowrap"
                >
                  {s}
                  <i className="ri-close-line"></i>
                </button>
              ))}
          </div>
        )}

        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            type="text"
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustom();
              }
            }}
            placeholder="목록에 없는 과목 직접 추가 (예: 논술)"
            className={`${inputClass} sm:flex-1`}
          />
          <button
            type="button"
            onClick={addCustom}
            className="flex items-center justify-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-200 whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            추가
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
        >
          {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
          성적 기록하기
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-700 transition hover:bg-background-200 whitespace-nowrap"
        >
          취소
        </button>
      </div>
    </form>
  );
}